import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { FormattedText } from "@/lib/tcf";
import { Rocket, ChevronDown, Quote, TrendingUp } from "lucide-react";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/tcf/templates").then((r) => setTemplates(r.data)).catch((e) => console.error("templates", e)); }, []);

  return (
    <div data-testid="templates-page">
      <h1 className="font-display text-4xl font-semibold">Jump Templates</h1>
      <p className="text-muted-foreground mt-2">Battle-tested frames to move up a level fast — memorize the structure, swap in your own ideas.</p>

      <div className="space-y-4 mt-8">
        {templates.map((t, i) => {
          const isOpen = open === t.id;
          return (
            <motion.div key={t.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="rounded-2xl bg-card border border-border overflow-hidden" data-testid={`template-${t.id}`}>
              <button onClick={() => setOpen(isOpen ? null : t.id)} className="w-full flex items-center gap-4 p-5 text-left">
                <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center shrink-0"><Rocket className="h-5 w-5 text-white" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-accent text-accent-foreground">{t.level_jump}</span>
                    <span className="text-xs text-muted-foreground truncate">{t.skill}</span>
                  </div>
                  <h3 className="font-semibold mt-1">{t.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-1">{t.summary}</p>
                </div>
                <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="px-5 pb-5 border-t border-border pt-4">
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">The structure</h4>
                      <ol className="space-y-2 mb-4">
                        {t.structure.map((s, si) => (
                          <li key={si} className="flex items-start gap-3 text-sm">
                            <span className="shrink-0 h-6 w-6 rounded-full bg-secondary grid place-items-center text-xs font-bold">{si + 1}</span>
                            <FormattedText text={s} />
                          </li>
                        ))}
                      </ol>
                      <div className="rounded-xl emerald-surface grain relative overflow-hidden p-4 text-white mb-3">
                        <div className="flex items-center gap-2 text-xs font-semibold text-white/80 mb-1"><Quote className="h-3.5 w-3.5" /> Example</div>
                        <p className="text-sm leading-relaxed">{t.example}</p>
                      </div>
                      <p className="text-sm text-muted-foreground flex items-start gap-2"><TrendingUp className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {t.why_it_scores}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
